import React, { useState, useMemo, useEffect } from "react"
import { Link, navigate } from "gatsby"
import {
  findInitialVariant,
  filteredAndSortedOptions,
  variantIdFromShopifyId,
  formatProductOptions,
} from "@utils/product"
import Button from "@components/button"
import { isEqual } from "lodash"
import { getUriFromDocument } from "@utils/routing"
import ColorSwatch from "@components/product/option-selector/swatch"
import ImageLabels from "@components/image/imageLabels"
import { getRegionShopUrl, isVisibleIn } from "@utils/region"
import {
  getPrice,
  getPriceFromVariantPresentmentPrices,
  getPricesFromContextualPricing,
  formattedPrice,
} from "@utils/products/pricing"
import { trackEvent, trackSelectItem } from "@src/services/analytics"
import { getImageUrl } from "@src/utils/image"
import { useQuickViewStore } from "@src/hooks/useStore"
import { useSiteSettingsStore } from "@src/hooks/useStore"
import Image from "@components/image";

const ProductItem = ({
  className,
  product,
  label,
  bundleLabel,
  twoDescLine,
  productInfos,
  buttonTheme,
  imageHeight,
  showPlusSign = true,
  cardFlags,
  index,
  fromList,
  hasFeaturedVariants,
  setShowCompareModal
}) => {
  const {
    isBundle,
    variants,
    bundleProductsList,
    bundleImages,
    discountAmount,
    productOptions,
    title,
    cardFlag,
    summary,
    shopifyId,
    productGroups,
    layout
  } = product;

  const { siteSettings } = useSiteSettingsStore(["siteSettings"]);
  const productsToCompare = siteSettings?.compareProducts?.products || [];
  const isProductInCompareList = productsToCompare.some(compareProduct => compareProduct.shopifyId === product?.shopifyId);

  const [currentVariant, setCurrentVariant] = useState( // initialVariant
    variants &&
    findInitialVariant(variants, true, hasFeaturedVariants)
  );
  const [currentProductUrl, setProductUrl] = useState( // prodUrl
    getUriFromDocument(product) + (
      (isBundle && layout != "product")
        ? ""
        : "?variantId=" + variantIdFromShopifyId(currentVariant?._id)
    )
  );
  const [productFromList, setProductFromList] = useState(fromList);

  const initialVariants = bundleProductsList?.map(bundleProduct =>
    findInitialVariant(bundleProduct?.variants)
  )
  const productTitle = isBundle && layout == "product" && bundleProductsList?.filter(product => isVisibleIn(product.visibilityRegions))?.length < 2 ? bundleProductsList?.[0]?.titleForBundle : title
  const atcLabel = (isBundle && layout != "product") ? bundleLabel : label

  const currentVariantImage = useMemo(() => {
    if (isBundle && layout != "product") {
      const firstValidImage = bundleImages?.find(
        image => image?.image?.asset?._id
      )
      if (firstValidImage) {
        return firstValidImage?.image
      }
      return bundleProductsList?.map(bundleProduct =>
        findInitialVariant(bundleProduct?.variants)?.variantImages?.[0] ||
        bundleProduct?.mainImage
      )
    }
    return currentVariant?.listingImage
  }, [isBundle, bundleImages, bundleProductsList, currentVariant, findInitialVariant])

  const currencyOptionPrice = useMemo(() => {
    if (isBundle && layout != "product") {
      const allPrices = initialVariants?.map(productVariant => {
        return getPricesFromContextualPricing(productVariant?.prices)
      })

      const allFallbackPrices = initialVariants?.map(productVariant =>
        parseFloat(productVariant?.price)
      )
      const priceValue = allPrices?.map((prices, index) => getPriceFromVariantPresentmentPrices(
        prices?.price,
        allFallbackPrices[index]
      ))

      return priceValue?.reduce((total, itemPrice) => total + itemPrice, 0)
    }

    const prices = getPricesFromContextualPricing(currentVariant?.prices);
    const fallbackPrice = parseFloat(currentVariant?.price);

    return prices ? getPriceFromVariantPresentmentPrices(
      prices?.price,
      fallbackPrice
    ) : currentVariant?.price
  }, [currentVariant])

  const price = (isBundle && layout != "product")
    ? formattedPrice(currencyOptionPrice - (currencyOptionPrice * discountAmount) / 100, initialVariants)
    || formattedPrice(currencyOptionPrice, initialVariants)
    : getPrice(currentVariant)

  const filteredOptions = useMemo(
    () =>
      filteredAndSortedOptions(
        formatProductOptions(productOptions),
        variants
      ),
    [productOptions]
  )

  const handleOptionChange = (optionIndex, { target }) => {
    const { value } = target
    const currentOptions = [...currentVariant.selectedOptions]

    if (currentOptions[optionIndex].value !== value) {
      currentOptions[optionIndex] = {
        ...currentOptions[optionIndex],
        value,
      }

      const selectedVariant = variants.find(
        ({ selectedOptions }) => isEqual(currentOptions, selectedOptions)
      )

      setCurrentVariant({ ...selectedVariant }, product)

      setProductUrl(
        getUriFromDocument(product) +
        ((isBundle && layout != "product") ? "" : "?variantId=" + selectedVariant?._id)
      )
    }
  }

  useEffect(() => {
    if (!productFromList) {
      setProductFromList(window.location.pathname)
    }
  }, [])

  const {
    setQuickViewOpen,
    setQuickViewProduct,
    setQuickViewVariant,
    setQuickViewType,
    setQuickViewProductList,
  } = useQuickViewStore(["setQuickViewOpen", "setQuickViewProduct", "setQuickViewVariant", "setQuickViewType", "setQuickViewProductList"])

  const openProductQuickView = () => {
    trackEvent("quick_view_opened", {});
    setQuickViewProduct(product)
    setQuickViewVariant(currentVariant)
    setQuickViewOpen(true)
    setQuickViewType("product")
    setQuickViewProductList(productFromList)
  }

  const handleLinkClick = () => {
    if (isBundle && layout != "product") {
      trackSelectItem(
        initialVariants.map(productVariant => {
          return {
            merchandise: {
              id: productVariant.shopifyId,
              sku: productVariant.sku,
              title: productVariant.variantTitle,
              price: Number(productVariant.price)?.toFixed(2),
              compareAtPrice: Number(productVariant.compareAtPrice)?.toFixed(2),
              image: {
                src: getImageUrl(productVariant.variantImages?.find(image => image._type == "image")),
              },
              product: {
                id: shopifyId,
                productTitle,
                onlineStoreUrl:
                  getRegionShopUrl() + getUriFromDocument(product),
                category: productGroups?.map(
                  group => group.title
                ),
              },
            },
            position: index,
          }
        }),
        window.location.pathname
      )
    } else {
      trackSelectItem(
        [
          {
            merchandise: {
              id: currentVariant.shopifyId,
              sku: currentVariant.sku,
              title: currentVariant.variantTitle,
              price: Number(currentVariant.price)?.toFixed(2),
              compareAtPrice: Number(currentVariant.compareAtPrice)?.toFixed(2),
              image: {
                src: getImageUrl(currentVariantImage),
              },
              product: {
                id: shopifyId,
                title: productTitle,
                onlineStoreUrl:
                  getRegionShopUrl() + getUriFromDocument(product),
                category: productGroups?.map(
                  group => group.title
                ),
              },
            },
            position: index,
          },
        ],
        window.location.pathname
      )
    }
  }

  return (
    <div className={`flex flex-col h-full mx-[5px] lg:mx-2.5 ${className}`}>
      <div className="h-full flex flex-col">
        <div
          className={`relative aspect-1 group h-auto max-w-full ${imageHeight ? imageHeight : ""
            }`}
        >
          <Link
            to={currentProductUrl}
            aria-label={`Product link: ${productTitle}`}
            title={productTitle}
            onClick={(e) => {
              e.preventDefault();
              handleLinkClick();
              navigate(currentProductUrl, {
                state: { fromPlpToPdp: true }
              });
            }}
          >
            <Image
              image={currentVariantImage}
              width={350}
              height={300}
              className={'rounded-xl w-full h-full'}
            />
          </Link>
          <ColorSwatch
            option={filteredOptions?.find(
              option => option.name === "Color" || option.name === "Flavor"
            )}
            handleOptionChange={handleOptionChange}
            variants={variants}
            currentVariant={currentVariant}
            compactWidth={true}
            // hideLabels={true}
            className={
              "testing absolute px-5 bottom-5 flex justify-center w-full gap-x-1.5 invisible opacity-0 transition-all md:group-hover:opacity-100 md:group-hover:visible max-md:opacity-100 max-md:visible"
            }
          />
          {cardFlags &&
            cardFlag &&
            cardFlag?.label && (<ImageLabels labels={cardFlag} />)
          }
          {isProductInCompareList &&
            <div className={`absolute p-2.5 md:p-5 flex gap-5 box-border right-0 top-0 cursor-pointer`} onClick={() => { setShowCompareModal(true) }}>
              <p className="py-1.5 px-2.5 z-[1] text-xs font-medium leading-3 rounded-md w-fit inline-flex items-center gap-1 bg-white">Compare</p>
            </div>
          }
        </div>
        <Button
          onClick={e => {
            e.stopPropagation()
            handleLinkClick()
            if (!isBundle && layout != "bundle") openProductQuickView()
            else navigate(currentProductUrl, {
              state: { fromPlpToPdp: true }
            })
          }}
          arrow="none"
          ctaWidth={"full"}
          theme={buttonTheme ? buttonTheme : "btn-transparent"}
          themeHover="btn-primary"
          className={"mt-5 md:mt-large"}
        >
          <div
            className="flex justify-between w-full items-center gap-x-2.5"
            aria-label={`${atcLabel
              ? `${atcLabel} for ${productTitle}, starting from ${price}, ${!isBundle && layout != "bundle" ? "launch quick view modal" : ""}`
              : `Shop ${productTitle}, starting from ${price}, ${!isBundle && layout != "bundle" ? "launch quick view modal" : ""}`
              } `}
          >
            <p className="font-medium text-left">{productTitle}</p>
            <p className="text-right product-card-price">
              {price ? price + (showPlusSign ? "+" : "") : ""}
            </p>
          </div>
        </Button>
        {productInfos && (
          <div className="flex flex-col gap-y-5 px-4 md:px-5 mt-5">
            <p className={`${twoDescLine ? "line-clamp-2" : "line-clamp-3"}`}>
              {summary}
            </p>
          </div>
        )}
      </div>
    </div >
  )
}

export default ProductItem
